import * as React from 'react'

import { Box, Button, Icon, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  CustomizationField,
  fieldLabel,
  typeLabel as getTypeLabel,
  useExpensesCustomizationUpdate,
} from 'modules/expenses/hooks/useExpensesCustomization'

import useFieldState from './useFieldState'

interface Props {
  fieldForDefault: CustomizationField
  fieldForInvoice: CustomizationField
  withDragAndDropIcon?: boolean
  onClickItemSettings?: (field: CustomizationField) => void
}

const SectionWrapper = ({ children }: { children: React.ReactNode }) => (
  <Box
    paddingTop="s16"
    flexDirection="row"
    justifyContent="spaceBetween"
    alignItems="center"
    width="full"
  >
    <Box
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="center"
      overflowX="hidden"
      width="full"
    >
      {children}
    </Box>
  </Box>
)

const ItemByDocument = ({
  fieldForDefault,
  fieldForInvoice,
  withDragAndDropIcon,
  onClickItemSettings = () => undefined,
}: Props) => {
  const { company: currentCompany } = useCurrentSession()
  const stateMap = {
    invoice: useFieldState(fieldForInvoice),
    default: useFieldState(fieldForDefault),
  }
  type StateMapKey = keyof typeof stateMap

  const handleFieldEnabled = async (key: StateMapKey) => {
    stateMap[key].toggleEnableLocally(async (field, newEnabled) => {
      const response = await updateField({
        companyId: currentCompany.id,
        id: field.id,
        enabled: newEnabled,
      })

      if (response.errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('expenses.settings.customization.update.error'),
        })
      } else {
        app.setFlash({
          type: 'correct',
          message: i18n.t('expenses.settings.customization.update.success'),
        })
      }
    })
  }

  const getEnabledAriaLabel = (field: CustomizationField) => {
    return field.enabled
      ? i18n.t('expenses.settings.customization.helpers.disable_field', { label })
      : i18n.t('expenses.settings.customization.helpers.enable_field', { field })
  }

  const getSubtitleLabel = (field: CustomizationField) => {
    return field.mandatory
      ? i18n.t('expenses_v2.default_fields.mandatory')
      : i18n.t('expenses_v2.default_fields.optional')
  }

  const onClick = (field: CustomizationField) => onClickItemSettings(field)
  const { updateField } = useExpensesCustomizationUpdate()
  const label = fieldLabel(fieldForInvoice) //dependent on the identifier, so it doesn't matter which field we choose

  return (
    <Box
      width="full"
      aria-label={i18n.t('expenses.settings.customization.helpers.field', { field: label })}
    >
      <Text size="300" weight="medium">
        {label}
      </Text>
      <SectionWrapper>
        <Box overflowX="hidden" paddingRight="s16" gap="s4">
          {withDragAndDropIcon && <Icon.Medium icon="drag-and-drop" />}
          <Text size="200" weight="medium">
            {i18n.t('expenses.settings.customization.helpers.by_document.for_invoices_label')}
          </Text>
          <Text size="200" weight="medium" color="grey600">
            {getSubtitleLabel(stateMap['invoice'].field)}
          </Text>
          <Text size="200" weight="medium" color="grey600">
            {getTypeLabel(stateMap['invoice'].field)}
          </Text>
        </Box>
        <Box gap="s16">
          <TogglerCheckbox
            disabled={!stateMap['invoice'].canChangeEnabled}
            value={stateMap['invoice'].enabled}
            onChange={() => handleFieldEnabled('invoice')}
            aria-label={getEnabledAriaLabel(stateMap['invoice'].field)}
          />
          <Button
            type="secondary"
            onClick={() => onClick(stateMap['invoice'].field)}
            aria-label={i18n.t('expenses.settings.customization.helpers.field_settings', {
              field: label,
            })}
            iconName="settings"
          />
        </Box>
      </SectionWrapper>
      <SectionWrapper>
        <Box overflowX="hidden" paddingRight="s16" gap="s4">
          {withDragAndDropIcon && <Icon.Medium icon="drag-and-drop" />}
          <Text size="200" weight="medium">
            {i18n.t('expenses.settings.customization.helpers.by_document.for_default_label')}
          </Text>
          <Text size="200" weight="medium" color="grey600">
            {getSubtitleLabel(stateMap['default'].field)}
          </Text>
          <Text size="200" weight="medium" color="grey600">
            {getTypeLabel(stateMap['default'].field)}
          </Text>
        </Box>
        <Box gap="s16">
          <TogglerCheckbox
            disabled={!stateMap['default'].canChangeEnabled}
            value={stateMap['default'].enabled}
            onChange={() => handleFieldEnabled('default')}
            aria-label={getEnabledAriaLabel(stateMap['default'].field)}
          />
          <Button
            type="secondary"
            onClick={() => onClick(stateMap['default'].field)}
            aria-label={i18n.t('expenses.settings.customization.helpers.field_settings', {
              field: label,
            })}
            iconName="settings"
          />
        </Box>
      </SectionWrapper>
    </Box>
  )
}

export default observer(ItemByDocument)
